import React from 'react';
import { useTranslation } from 'react-i18next';

import { Root, Label, InputRow, Affix, ActionSlot, Input } from './Field.styles';

export type FieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Leading affix, e.g. "$". */
  prefix?: string;
  /** Trailing affix, e.g. "per FTD" or "%". */
  suffix?: string;
  /**
   * A control rendered in the trailing affix slot, in place of `suffix`. Same position and metrics
   * as the text affix, so a field can carry an inline action (e.g. Regenerate) without a second
   * row. `suffix` wins if both are passed, though no caller should pass both.
   *
   * FOLLOW-UP: `suffixAction` and `action` (below) are two props for a trailing control, added in
   * parallel on two branches and kept side by side at the merge rather than unified mid-merge.
   * `action` has live callers (the affiliates CampaignForm URL fields); `suffixAction` has none in
   * the app today (its last caller went with the deleted mailboxes scenario) and is exercised only
   * by Field.ct.tsx. Settle on one with whoever added `action` (e4290bdc, Catalin Mihai Uta
   * Draghici).
   */
  suffixAction?: React.ReactNode;
  /**
   * Optional trailing control rendered inside the input row, after the value (e.g. an
   * open-in-new-tab icon button on a URL field). It is a sibling of the input, not part of
   * the editable value; the caller owns its behaviour and accessible name. See the FOLLOW-UP
   * note on `suffixAction`.
   */
  action?: React.ReactNode;
  /** Hint a numeric keypad on mobile. */
  numeric?: boolean;
  /** Non-editable (e.g. a published article's locked slug). */
  disabled?: boolean;
  /**
   * Hard cap the browser holds while typing or pasting. It does NOT trim a
   * longer value the field is handed, so a value stored before the cap existed
   * still shows and still saves.
   */
  maxLength?: number;
};

/** Labelled text input. Label/affixes via `<Text>`; placeholder/aria-label via `t()`. */
export const Field = ({
  label,
  value,
  onChange,
  placeholder,
  prefix,
  suffix,
  suffixAction,
  numeric = false,
  disabled = false,
  maxLength,
  action,
}: FieldProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Root $disabled={disabled}>
      <Label text={label} tag="span" />
      <InputRow>
        {prefix ? <Affix text={prefix} tag="span" /> : null}
        <Input
          type="text"
          inputMode={numeric ? 'decimal' : 'text'}
          value={value}
          aria-label={t(label)}
          placeholder={placeholder ? t(placeholder) : undefined}
          disabled={disabled}
          maxLength={maxLength}
          onChange={(event) => onChange(event.target.value)}
        />
        {suffix ? <Affix text={suffix} tag="span" /> : suffixAction ? <ActionSlot>{suffixAction}</ActionSlot> : null}
        {action}
      </InputRow>
    </Root>
  );
};

export default Field;
